<template>
  <div class="map-container" ref="mapContainer">
    <div class="map-toolbar">
      <button
          :class="['toolbar-btn', { active: drawMode === 'polygon' }]"
          @click="toggleDrawMode"
          title="Draw polygon to select devices"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <polygon points="3 6 9 3 15 6 21 3 21 18 15 21 9 18 3 21"></polygon>
        </svg>
      </button>
      <button
          class="toolbar-btn"
          @click="clearPolygon"
          title="Clear selection"
          :disabled="!hasPolygon"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <polyline points="3 6 5 6 21 6"></polyline>
          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
        </svg>
      </button>
    </div>
    <div class="selection-info" v-if="devicesInPolygon.length > 0">
      <p><strong>{{ devicesInPolygon.length }}</strong> devices selected</p>
    </div>
  </div>
</template>

<script>
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import * as turf from '@turf/turf';
import {
  TerraDraw,
  TerraDrawPolygonMode,
  TerraDrawSelectMode,
  TerraDrawRenderMode
} from 'terra-draw';
import { TerraDrawMapLibreGLAdapter } from 'terra-draw-maplibre-gl-adapter';

export default {
  name: 'DriveByRouteMap',
  props: {
    selectedRoute: {
      type: Object,
      default: null
    },
    devices: {
      type: Array,
      default: () => []
    },
    selectedDevices: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      map: null,
      draw: null,
      sourceLoaded: false,
      devicesInPolygon: [],
      drawMode: 'render',
      hasPolygon: false,
      currentPolygonId: null
    };
  },
  mounted() {
    this.initializeMap();
  },
  beforeUnmount() {
    if (this.draw) {
      this.draw.stop();
    }
    if (this.map) {
      this.map.remove();
    }
  },
  watch: {
    devices: {
      handler() {
        if (this.sourceLoaded) {
          this.updateDeviceData();
        }
      },
      deep: true
    },
    selectedDevices: {
      handler() {
        if (this.sourceLoaded) {
          this.updateDeviceData();
        }
      },
      deep: true
    },
    selectedRoute: {
      handler(newRoute) {
        if (newRoute) {
          this.displayRoute(newRoute);
        } else {
          this.clearRoute();
        }
      },
      deep: true
    }
  },
  methods: {
    initializeMap() {
      this.map = new maplibregl.Map({
        container: this.$refs.mapContainer,
        style: 'https://api.maptiler.com/maps/basic-v2-dark/style.json?key=4DXCyU0jQcYTU9cFAGQu',
        center: [9.1859, 45.4654],
        zoom: 12,
        attributionControl: false
      });

      this.map.addControl(new maplibregl.NavigationControl(), 'top-right');
      this.map.addControl(new maplibregl.AttributionControl({ compact: true }), 'bottom-left');

      this.map.on('load', () => {
        this.setupDeviceLayers();
        this.addRouteLayer();
        this.initTerraDraw();
        this.sourceLoaded = true;

        if (this.devices.length > 0) {
          this.updateDeviceData();
        }
      });
    },

    initTerraDraw() {
      this.draw = new TerraDraw({
        adapter: new TerraDrawMapLibreGLAdapter({ map: this.map }),
        modes: [
          new TerraDrawPolygonMode(),
          new TerraDrawSelectMode({
            flags: {
              polygon: {
                feature: {
                  draggable: false,
                  coordinates: {
                    midpoints: false,
                    draggable: false,
                    deletable: false
                  }
                }
              }
            }
          }),
          new TerraDrawRenderMode({ modeName: 'render' })
        ]
      });

      this.draw.start();

      this.draw.on('finish', (id) => {
        this.currentPolygonId = id;
        this.hasPolygon = true;
        this.drawMode = 'render';
        this.draw.setMode('render');
        this.processPolygonSelection();
      });

      this.draw.on('change', (ids, type) => {
        if (type === 'delete') {
          this.hasPolygon = false;
          this.currentPolygonId = null;
          this.devicesInPolygon = [];
          this.$emit('devices-selected', []);
          this.updateDeviceData();
        }
      });
    },

    toggleDrawMode() {
      if (this.drawMode === 'polygon') {
        this.drawMode = 'render';
        this.draw.setMode('render');
      } else {
        this.clearPolygon();
        this.drawMode = 'polygon';
        this.draw.setMode('polygon');
      }
    },

    clearPolygon() {
      if (this.draw) {
        const snapshot = this.draw.getSnapshot();
        if (snapshot && snapshot.length > 0) {
          try {
            this.draw.removeFeatures(snapshot.map(f => f.id));
          } catch (e) {
            // Features may already be gone
          }
        }
      }

      this.hasPolygon = false;
      this.currentPolygonId = null;
      this.devicesInPolygon = [];
      this.$emit('devices-selected', []);
      this.updateDeviceData();
    },

    processPolygonSelection() {
      const snapshot = this.draw.getSnapshot();
      if (!snapshot || snapshot.length === 0) return;

      const polygon = snapshot.find(f => f.geometry.type === 'Polygon');
      if (!polygon) return;

      const devicesInPolygon = [];

      this.devices.forEach(device => {
        const mp = device.metering_point;
        if (!mp || !mp.lat || !mp.lon) return;

        const lat = parseFloat(mp.lat);
        const lon = parseFloat(mp.lon);
        if (isNaN(lat) || isNaN(lon)) return;

        const point = turf.point([lon, lat]);
        if (turf.booleanPointInPolygon(point, polygon.geometry)) {
          devicesInPolygon.push(device);
        }
      });

      this.devicesInPolygon = devicesInPolygon;
      this.$emit('devices-selected', devicesInPolygon);
      this.updateDeviceData();
    },

    setupDeviceLayers() {
      this.map.addSource('devices', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
        cluster: true,
        clusterRadius: 50,
        clusterProperties: {
          'total': ['+', 1],
          'polygon_selected_count': ['+', ['case', ['get', 'is_polygon_selected'], 1, 0]]
        }
      });

      this.map.addLayer({
        id: 'clusters',
        type: 'circle',
        source: 'devices',
        filter: ['has', 'point_count'],
        paint: {
          'circle-color': [
            'case',
            ['==', ['get', 'polygon_selected_count'], ['get', 'point_count']], '#00e676',
            ['>', ['get', 'polygon_selected_count'], 0], '#69f0ae',
            '#4fc3f7'
          ],
          'circle-radius': ['step', ['get', 'point_count'], 20, 100, 30, 750, 40],
          'circle-stroke-width': 1,
          'circle-stroke-color': '#fff',
          'circle-opacity': 0.8
        }
      });

      this.map.addLayer({
        id: 'cluster-count',
        type: 'symbol',
        source: 'devices',
        filter: ['has', 'point_count'],
        layout: {
          'text-field': '{point_count_abbreviated}',
          'text-size': 12
        },
        paint: { 'text-color': '#ffffff' }
      });

      this.map.addLayer({
        id: 'unclustered-point',
        type: 'circle',
        source: 'devices',
        filter: ['!', ['has', 'point_count']],
        paint: {
          'circle-color': [
            'case',
            ['get', 'is_polygon_selected'], '#00e676',
            ['get', 'is_selected'], '#4fc3f7',
            ['==', ['get', 'days_since_last_reading'], -1], '#ff8a80',
            ['>', ['get', 'days_since_last_reading'], 30], '#ffc107',
            'rgba(255, 255, 255, 0.8)'
          ],
          'circle-radius': ['case', ['get', 'is_polygon_selected'], 8, 6],
          'circle-stroke-width': ['case', ['get', 'is_polygon_selected'], 2, 1],
          'circle-stroke-color': '#fff',
          'circle-opacity': 0.9
        }
      });

      this.map.on('click', 'clusters', async (e) => {
        const features = this.map.queryRenderedFeatures(e.point, { layers: ['clusters'] });
        if (!features.length) return;
        const clusterId = features[0].properties.cluster_id;
        const source = this.map.getSource('devices');
        try {
          const zoom = await source.getClusterExpansionZoom(clusterId);
          this.map.easeTo({ center: features[0].geometry.coordinates, zoom });
        } catch (err) {
          console.error('Cluster zoom error:', err);
        }
      });

      this.map.on('click', 'unclustered-point', (e) => {
        const features = this.map.queryRenderedFeatures(e.point, { layers: ['unclustered-point'] });
        if (!features.length) return;

        const coordinates = features[0].geometry.coordinates.slice();
        const props = features[0].properties;

        while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
          coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
        }

        new maplibregl.Popup({ offset: 25 })
            .setLngLat(coordinates)
            .setHTML(`
          <div class="marker-popup">
            <h4>Device: ${props.logical_device_number || 'Unknown'}</h4>
            <p><strong>MPID:</strong> ${props.active_mpid || 'N/A'}</p>
            <p><strong>Location:</strong> ${props.street || 'N/A'}, ${props.city || 'N/A'}</p>
            <p><strong>Last Reading:</strong> ${
                props.days_since_last_reading === -1
                    ? '<span style="color: #ff8a80;">Never</span>'
                    : props.days_since_last_reading + ' days ago'
            }</p>
          </div>
        `)
            .addTo(this.map);
      });

      this.map.on('mouseenter', 'clusters', () => { this.map.getCanvas().style.cursor = 'pointer'; });
      this.map.on('mouseleave', 'clusters', () => { this.map.getCanvas().style.cursor = ''; });
      this.map.on('mouseenter', 'unclustered-point', () => { this.map.getCanvas().style.cursor = 'pointer'; });
      this.map.on('mouseleave', 'unclustered-point', () => { this.map.getCanvas().style.cursor = ''; });
    },

    updateDeviceData() {
      if (!this.map || !this.sourceLoaded) return;
      const source = this.map.getSource('devices');
      if (!source) return;

      const features = this.devices
          .filter(device => {
            const mp = device.metering_point;
            return mp && mp.lat && mp.lon &&
                !isNaN(parseFloat(mp.lat)) &&
                !isNaN(parseFloat(mp.lon));
          })
          .map(device => {
            const mp = device.metering_point;
            const isSelected = this.selectedDevices.some(
                d => d.logical_device_number === device.logical_device_number
            );
            const isPolygonSelected = this.devicesInPolygon.some(
                d => d.logical_device_number === device.logical_device_number
            );

            return {
              type: 'Feature',
              geometry: {
                type: 'Point',
                coordinates: [parseFloat(mp.lon), parseFloat(mp.lat)]
              },
              properties: {
                logical_device_number: device.logical_device_number || '',
                active_mpid: device.active_mpid || '',
                street: mp.street || '',
                city: mp.city || '',
                days_since_last_reading:
                    device.days_since_last_reading !== undefined
                        ? device.days_since_last_reading : -1,
                is_selected: isSelected,
                is_polygon_selected: isPolygonSelected
              }
            };
          });

      source.setData({ type: 'FeatureCollection', features });

      if (features.length > 0 && !this.hasPolygon) {
        const bounds = new maplibregl.LngLatBounds();
        features.forEach(f => bounds.extend(f.geometry.coordinates));
        this.map.fitBounds(bounds, {
          padding: { top: 100, bottom: 100, left: 100, right: 100 },
          maxZoom: 12,
          duration: 1000
        });
      }
    },

    addRouteLayer() {
      this.map.addSource('route', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] }
      });

      this.map.addLayer({
        id: 'route-line',
        type: 'line',
        source: 'route',
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: { 'line-color': '#4fc3f7', 'line-width': 3, 'line-opacity': 0.8 }
      });
    },

    displayRoute(route) {
      if (!route || !route.meter_readings) return;
      // Route devices are shown as clusters/dots via the devices watcher.
      // No route line is drawn since meter readings have no sequential order.
      this.clearRoute();
    },

    clearRoute() {
      if (this.map && this.map.getSource('route')) {
        this.map.getSource('route').setData({ type: 'FeatureCollection', features: [] });
      }
    }
  }
};
</script>
<style scoped>
.map-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: #1a1a1a;
}

.map-toolbar {
  position: absolute;
  top: 10px;
  right: 50px; /* offset from navigation controls */
  z-index: 10;
  display: flex;
  flex-direction: column;
  gap: 4px;
  background: rgba(30, 30, 30, 0.9);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  padding: 4px;
}

.toolbar-btn {
  width: 30px;
  height: 30px;
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.8);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 3px;
  transition: all 0.2s;
}

.toolbar-btn:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.1);
  color: white;
}

.toolbar-btn.active {
  background: rgba(79, 195, 247, 0.2);
  color: #4fc3f7;
}

.toolbar-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.selection-info {
  position: absolute;
  bottom: 40px;
  left: 10px;
  background-color: rgba(30, 30, 30, 0.95);
  color: white;
  padding: 15px 20px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);
  min-width: 180px;
  text-align: center;
}

.selection-info p {
  margin: 5px 0;
  font-size: 13px;
  font-family: 'Inter', sans-serif;
}

.selection-info strong {
  color: #4fc3f7;
  font-size: 16px;
}

/* Popup styles */
:deep(.maplibregl-popup-content) {
  background-color: rgba(30, 30, 30, 0.95);
  color: white;
  padding: 16px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

:deep(.maplibregl-popup-close-button) {
  color: rgba(255, 255, 255, 0.8);
  font-size: 18px;
}

:deep(.maplibregl-popup-close-button:hover) {
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
}

:deep(.marker-popup h4) {
  margin: 0 0 12px 0;
  font-size: 15px;
  font-weight: 600;
  color: #4fc3f7;
}

:deep(.marker-popup p) {
  margin: 6px 0;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.9);
}

:deep(.marker-popup strong) {
  font-weight: 600;
  color: rgba(255, 255, 255, 0.7);
  margin-right: 4px;
}

/* terra-draw polygon styling */
:deep(.td-polygon-fill) {
  fill: rgba(79, 195, 247, 0.15);
}

:deep(.td-polygon-outline) {
  stroke: #4fc3f7;
  stroke-width: 2;
}
</style>